import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  TrendingUp, TrendingDown, ShieldCheck, ShieldOff, Download,
  ArrowUp, ArrowDown, Filter, Calendar, Clock,
} from 'lucide-react';
import {
  BOT_COLORS, BOT_LABELS, WEEKDAY_LABELS, TIMEFRAMES,
  formatUSD, formatUSDCompact, formatPct, formatNum, formatTs,
  tradesToCsv, downloadCsv,
} from './ops_helpers';
import { AnalyticsKpiGrid } from './AnalyticsKpiGrid';

const tooltipStyle = {
  backgroundColor: '#0a0a0b',
  border: '1px solid #3f3f46',
  borderRadius: '8px',
  fontSize: '12px',
};

// =============================================================================
// Cumulative PnL Chart
// =============================================================================
const CumulativePnLChart = ({ series, color }) => {
  const data = useMemo(() => series.map((p) => ({
    t: p.t,
    label: new Date(p.t).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    pnl: p.cum_pnl,
    peak: p.peak,
  })), [series]);

  if (data.length < 2) {
    return <p className="text-center text-zinc-500 py-10 text-sm">No hay suficientes trades para dibujar el gráfico.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`cum-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.5} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 10 }} minTickGap={40} />
        <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => formatUSDCompact(v)} />
        <Tooltip
          contentStyle={tooltipStyle}
          labelStyle={{ color: '#a1a1aa' }}
          formatter={(v, n) => [formatUSD(v), n === 'pnl' ? 'PnL acum.' : 'Peak']}
        />
        <ReferenceLine y={0} stroke="#52525b" strokeDasharray="3 3" />
        <Area type="monotone" dataKey="pnl" stroke={color} strokeWidth={2} fill={`url(#cum-${color})`} />
        <Line type="monotone" dataKey="peak" stroke="#facc15" strokeDasharray="4 4" strokeWidth={1} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// =============================================================================
// Underwater Drawdown Chart
// =============================================================================
const UnderwaterChart = ({ series }) => {
  const data = useMemo(() => series.map((p) => ({
    t: p.t,
    label: new Date(p.t).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    dd_pct: p.dd_pct,
    dd_abs: p.dd_abs,
  })), [series]);

  if (data.length < 2) {
    return <p className="text-center text-zinc-500 py-10 text-sm">Sin datos.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="dd-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 10 }} minTickGap={40} />
        <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v.toFixed(1)}%`} />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(v, n) => [n === 'dd_pct' ? formatPct(v, 2) : formatUSD(v), n === 'dd_pct' ? 'DD %' : 'DD $']}
        />
        <Area type="monotone" dataKey="dd_pct" stroke="#f43f5e" strokeWidth={2} fill="url(#dd-grad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// =============================================================================
// PnL per Trade (bars verde/roja)
// =============================================================================
const PnLPerTradeChart = ({ series }) => {
  const data = useMemo(() => series.map((p, i) => ({
    idx: i + 1,
    pnl: p.pnl,
    label: new Date(p.t).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    market: p.market,
    strategy: p.strategy,
  })), [series]);

  if (data.length < 1) {
    return <p className="text-center text-zinc-500 py-10 text-sm">Sin trades.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="idx" stroke="#71717a" tick={{ fontSize: 10 }} label={{ value: 'Trade #', position: 'insideBottom', fill: '#71717a', fontSize: 10, offset: -5 }} />
        <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => formatUSDCompact(v)} />
        <ReferenceLine y={0} stroke="#52525b" />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(v) => [formatUSD(v), 'PnL']}
          labelFormatter={(idx) => {
            const row = data[idx - 1];
            return row ? `#${idx} · ${row.market} · ${row.label}` : `#${idx}`;
          }}
        />
        <Bar dataKey="pnl">
          {data.map((d, i) => (
            <Cell key={i} fill={d.pnl >= 0 ? '#10b981' : '#f43f5e'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// =============================================================================
// Win/Loss Donut
// =============================================================================
const WinLossDonut = ({ wins, losses, breakeven }) => {
  const data = [
    { name: 'Wins', value: wins, fill: '#10b981' },
    { name: 'Losses', value: losses, fill: '#f43f5e' },
    ...(breakeven > 0 ? [{ name: 'Breakeven', value: breakeven, fill: '#71717a' }] : []),
  ].filter((d) => d.value > 0);

  if (data.length === 0) return <p className="text-center text-zinc-500 py-6 text-sm">Sin trades.</p>;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          formatter={(v, e) => <span className="text-zinc-300 text-xs">{v}: {e.payload.value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

// =============================================================================
// Hour of Day Heatmap (24 buckets)
// =============================================================================
const HourHeatmap = ({ data }) => {
  const maxAbs = useMemo(() => {
    return Math.max(1, ...data.map((d) => Math.abs(d.pnl)));
  }, [data]);

  const cellColor = (pnl) => {
    if (pnl === 0) return 'bg-zinc-900 border-zinc-800';
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    if (pnl > 0) {
      const a = Math.round(20 + intensity * 65);
      return `border-emerald-800/40`;
    } else {
      return `border-rose-800/40`;
    }
  };

  const cellStyle = (pnl) => {
    if (pnl === 0) return {};
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    const opacity = (0.15 + intensity * 0.55).toFixed(2);
    return {
      backgroundColor: pnl > 0
        ? `rgba(16, 185, 129, ${opacity})`
        : `rgba(244, 63, 94, ${opacity})`,
    };
  };

  return (
    <div className="grid grid-cols-12 gap-1.5">
      {data.map((h) => (
        <div
          key={h.hour}
          className={`p-2 rounded border ${cellColor(h.pnl)} text-center text-[10px]`}
          style={cellStyle(h.pnl)}
          title={`${h.hour}:00 UTC · ${h.trades} trades · WR ${h.win_rate}% · PnL ${formatUSD(h.pnl)}`}
        >
          <div className="text-zinc-400 font-mono">{String(h.hour).padStart(2, '0')}h</div>
          <div className={`font-bold ${h.pnl > 0 ? 'text-emerald-300' : h.pnl < 0 ? 'text-rose-300' : 'text-zinc-600'}`}>
            {h.trades > 0 ? formatUSDCompact(h.pnl) : '—'}
          </div>
          <div className="text-zinc-500 text-[9px]">{h.trades || '·'}t</div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// Weekday Heatmap (7 buckets)
// =============================================================================
const WeekdayBars = ({ data }) => {
  const chartData = data.map((d, i) => ({
    day: WEEKDAY_LABELS[i],
    pnl: d.pnl,
    trades: d.trades,
    win_rate: d.win_rate,
  }));

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="day" stroke="#71717a" tick={{ fontSize: 11 }} />
        <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => formatUSDCompact(v)} />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(v, n, p) => {
            if (n === 'pnl') return [formatUSD(v), 'PnL'];
            return [v, n];
          }}
          labelFormatter={(d, p) => {
            const t = p?.[0]?.payload?.trades || 0;
            const w = p?.[0]?.payload?.win_rate || 0;
            return `${d} · ${t} trades · WR ${w}%`;
          }}
        />
        <ReferenceLine y={0} stroke="#52525b" />
        <Bar dataKey="pnl">
          {chartData.map((d, i) => (
            <Cell key={i} fill={d.pnl >= 0 ? '#10b981' : '#f43f5e'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// =============================================================================
// Exit reason / Strategy breakdown table
// =============================================================================
const BreakdownTable = ({ rows, labelKey, label, showProfitFactor = false }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-center text-zinc-500 py-4 text-xs">Sin datos.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow className="border-zinc-800 hover:bg-transparent">
          <TableHead className="text-zinc-400 text-xs">{label}</TableHead>
          <TableHead className="text-zinc-400 text-xs text-right">#</TableHead>
          <TableHead className="text-zinc-400 text-xs text-right">WR</TableHead>
          {showProfitFactor && <TableHead className="text-zinc-400 text-xs text-right">PF</TableHead>}
          <TableHead className="text-zinc-400 text-xs text-right">PnL</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i} className="border-zinc-800 hover:bg-zinc-800/30 text-xs">
            <TableCell className="text-zinc-200 font-mono">{r[labelKey] || 'n/a'}</TableCell>
            <TableCell className="text-right text-zinc-300">{r.count}</TableCell>
            <TableCell className="text-right text-zinc-300">{formatPct(r.win_rate, 1)}</TableCell>
            {showProfitFactor && (
              <TableCell className="text-right text-zinc-300">
                {r.profit_factor == null ? '—' : formatNum(r.profit_factor, 2)}
              </TableCell>
            )}
            <TableCell className={`text-right font-semibold ${r.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatUSD(r.pnl)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// =============================================================================
// Trades Table con sort, search, paginación, CSV
// =============================================================================
const TradesTable = ({ trades, botId }) => {
  const [sort, setSort] = useState({ key: 'closed_at', dir: 'desc' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let rows = trades || [];
    if (q) {
      rows = rows.filter((t) =>
        (t.market || '').toLowerCase().includes(q) ||
        (t.strategy || '').toLowerCase().includes(q) ||
        (t.direction || '').toLowerCase().includes(q) ||
        (t.exit_reason || '').toLowerCase().includes(q) ||
        (t.trade_id || '').toLowerCase().includes(q)
      );
    }
    rows = [...rows].sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return rows;
  }, [trades, search, sort]);

  const total = filtered.length;
  const totalPages = Math.ceil(total / pageSize);
  const pageRows = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const headerCell = (key, label, align = 'left') => (
    <TableHead
      className={`text-zinc-400 text-xs cursor-pointer hover:text-violet-300 ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => {
        setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
        setPage(0);
      }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === key && (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </TableHead>
  );

  const exportCsv = () => {
    const csv = tradesToCsv(filtered);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCsv(`${botId}-trades-${ts}.csv`, csv);
  };

  return (
    <div className="space-y-3" data-testid="trades-table">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Input
            data-testid="trades-search-input"
            placeholder="Buscar mercado, estrategia, dirección…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 text-xs h-8 max-w-[320px]"
          />
          <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
            {total} trades
          </Badge>
        </div>
        <Button
          data-testid="trades-export-csv-btn"
          size="sm" variant="outline"
          onClick={exportCsv}
          className="h-8 border-zinc-700 hover:border-violet-500/50 text-xs"
        >
          <Download className="w-3.5 h-3.5 mr-1" />
          Exportar CSV
        </Button>
      </div>

      <div className="max-h-[480px] overflow-y-auto rounded-lg border border-zinc-800">
        <Table>
          <TableHeader className="sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {headerCell('closed_at', 'Cerrado')}
              {headerCell('market', 'Mercado')}
              {headerCell('strategy', 'Estrategia')}
              {headerCell('direction', 'Dir')}
              {headerCell('entry_price', 'Entry', 'right')}
              {headerCell('sl_price', 'SL', 'right')}
              {headerCell('tp_price', 'TP', 'right')}
              {headerCell('exit_price', 'Exit', 'right')}
              {headerCell('pnl_usd', 'PnL', 'right')}
              {headerCell('exit_reason', 'Razón')}
              {headerCell('duration_sec', 'Dur.', 'right')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((t, i) => (
              <TableRow key={`${t.trade_id || i}`} className="border-zinc-800 text-xs hover:bg-zinc-800/30">
                <TableCell className="text-zinc-500 font-mono whitespace-nowrap">{formatTs(t.closed_at || t.opened_at)}</TableCell>
                <TableCell className="text-zinc-200 font-semibold">{t.market}</TableCell>
                <TableCell className="text-zinc-300 font-mono text-[10px]">{t.strategy || '—'}</TableCell>
                <TableCell>
                  <Badge className={`text-[10px] ${(t.direction === 'long' || t.direction === 'up') ? 'bg-emerald-700' : 'bg-rose-700'}`}>
                    {t.direction}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-zinc-400 font-mono">{t.entry_price}</TableCell>
                <TableCell className="text-right text-rose-300/70 font-mono">{t.sl_price ?? '—'}</TableCell>
                <TableCell className="text-right text-emerald-300/70 font-mono">{t.tp_price ?? '—'}</TableCell>
                <TableCell className="text-right text-zinc-400 font-mono">{t.exit_price ?? '—'}</TableCell>
                <TableCell className={`text-right font-bold ${(t.pnl_usd ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {t.pnl_usd != null ? `${t.pnl_usd >= 0 ? '+' : ''}${formatUSD(t.pnl_usd)}` : '—'}
                </TableCell>
                <TableCell className="text-zinc-500">{t.exit_reason || '—'}</TableCell>
                <TableCell className="text-right text-zinc-500 font-mono">
                  {t.duration_sec ? `${Math.round(t.duration_sec / 60)}m` : '—'}
                </TableCell>
              </TableRow>
            ))}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-zinc-500 py-10">
                  Sin trades para los filtros actuales.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <div>Página {page + 1} / {totalPages}</div>
          <div className="flex gap-2">
            <Button data-testid="trades-prev-page-btn" size="sm" variant="outline" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="h-7 border-zinc-700 text-xs">← Anterior</Button>
            <Button data-testid="trades-next-page-btn" size="sm" variant="outline" disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="h-7 border-zinc-700 text-xs">Siguiente →</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Timeframe Selector
// =============================================================================
const TimeframeSelector = ({ value, onChange, testIdPrefix = 'tf' }) => {
  return (
    <div className="inline-flex items-center gap-0.5 bg-zinc-900/80 border border-zinc-800 rounded-lg p-0.5">
      {TIMEFRAMES.map((tf) => (
        <button
          key={tf.id}
          data-testid={`${testIdPrefix}-${tf.id}`}
          onClick={() => onChange(tf.days)}
          className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
            value === tf.days
              ? 'bg-violet-600 text-white shadow-md'
              : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
          }`}
        >
          {tf.label}
        </button>
      ))}
    </div>
  );
};

// =============================================================================
// Filters bar (strategy + market)
// =============================================================================
const FiltersBar = ({ strategies, markets, strategy, market, onStrategyChange, onMarketChange }) => {
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <Filter className="w-3.5 h-3.5 text-zinc-500" />
      <span className="text-zinc-500">Filtros:</span>
      {strategies.length > 0 && (
        <Select value={strategy || 'all'} onValueChange={(v) => onStrategyChange(v === 'all' ? null : v)}>
          <SelectTrigger data-testid="strategy-filter" className="h-8 w-auto min-w-[160px] bg-zinc-900 border-zinc-800 text-xs">
            <SelectValue placeholder="Estrategia" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all" className="text-xs">Todas las estrategias</SelectItem>
            {strategies.map((s) => (
              <SelectItem key={s} value={s} className="text-xs font-mono">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {markets.length > 1 && (
        <Select value={market || 'all'} onValueChange={(v) => onMarketChange(v === 'all' ? null : v)}>
          <SelectTrigger data-testid="market-filter" className="h-8 w-auto min-w-[140px] bg-zinc-900 border-zinc-800 text-xs">
            <SelectValue placeholder="Mercado" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all" className="text-xs">Todos los mercados</SelectItem>
            {markets.map((m) => (
              <SelectItem key={m} value={m} className="text-xs font-mono">{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT — BotAnalytics (un tab por bot)
// =============================================================================
export const BotAnalytics = ({
  bot,                      // bot state object {bot_id, equity_usd, ...}
  analytics,                // payload del endpoint /api/ops/bots/{id}/analytics
  trades,                   // raw trades list (para tabla y series ya pre-cargada)
  days, onDaysChange,       // timeframe selector
  strategy, onStrategyChange,
  market, onMarketChange,
  loading,
}) => {
  const color = BOT_COLORS[bot.bot_id] || '#a855f7';
  const series = analytics?.series || { cumulative_pnl: [], underwater: [], pnl_per_trade: [], by_hour: [], by_weekday: [] };

  return (
    <div className="space-y-4" data-testid={`bot-analytics-${bot.bot_id}`}>
      {/* Toolbar: timeframe + filtros */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-zinc-400 font-medium">Timeframe:</span>
            <TimeframeSelector value={days} onChange={onDaysChange} testIdPrefix={`tf-${bot.bot_id}`} />
          </div>
          <FiltersBar
            strategies={analytics?.available_strategies || []}
            markets={analytics?.available_markets || []}
            strategy={strategy}
            market={market}
            onStrategyChange={onStrategyChange}
            onMarketChange={onMarketChange}
          />
        </CardContent>
      </Card>

      {/* KPI Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-24 bg-zinc-900/60 border border-zinc-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <AnalyticsKpiGrid analytics={analytics} />
      )}

      {/* Cumulative PnL (full width) */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color }} />
            PnL acumulado
          </CardTitle>
          <CardDescription>
            {analytics?.trades_count || 0} trades · línea amarilla = peak histórico
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CumulativePnLChart series={series.cumulative_pnl} color={color} />
        </CardContent>
      </Card>

      {/* Grid 2 cols: Underwater + PnL per Trade */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-rose-400" />
              Drawdown underwater
            </CardTitle>
            <CardDescription>
              Caída desde peak. Max DD: <span className="text-rose-400 font-semibold">{formatPct(analytics?.max_drawdown_pct || 0, 2)}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UnderwaterChart series={series.underwater} />
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">PnL por trade</CardTitle>
            <CardDescription>
              Verde = win, rojo = loss
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PnLPerTradeChart series={series.pnl_per_trade} />
          </CardContent>
        </Card>
      </div>

      {/* Grid 3 cols: Win/Loss donut + Weekday bars + Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">Win / Loss</CardTitle>
            <CardDescription>{analytics?.wins || 0}W · {analytics?.losses || 0}L</CardDescription>
          </CardHeader>
          <CardContent>
            <WinLossDonut wins={analytics?.wins || 0} losses={analytics?.losses || 0} breakeven={analytics?.breakeven || 0} />
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">PnL por día de la semana</CardTitle>
            <CardDescription>Cuándo es más rentable</CardDescription>
          </CardHeader>
          <CardContent>
            <WeekdayBars data={series.by_weekday} />
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">Exit reasons</CardTitle>
            <CardDescription>Cómo cierran tus trades</CardDescription>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={(analytics?.exit_reason_breakdown || []).slice(0, 6)}
              labelKey="reason"
              label="Razón"
            />
          </CardContent>
        </Card>
      </div>

      {/* Hour heatmap (full width) */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-violet-400" />
            Distribución horaria (UTC)
          </CardTitle>
          <CardDescription>
            PnL acumulado por hora del día. Útil para detectar sesiones rentables.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <HourHeatmap data={series.by_hour} />
        </CardContent>
      </Card>

      {/* Strategy breakdown (especialmente útil para FTMO con LST + London Breakout) */}
      {(analytics?.strategy_breakdown || []).length > 1 && (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">Performance por estrategia</CardTitle>
            <CardDescription>Comparativa de cada algoritmo activo en este bot</CardDescription>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={analytics.strategy_breakdown}
              labelKey="strategy"
              label="Estrategia"
              showProfitFactor
            />
          </CardContent>
        </Card>
      )}

      {/* Filtros activos del bot (mantener del original) */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> Filtros activos del bot
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bot.filters_active ? (
            <div className="flex flex-wrap gap-2">
              {Object.entries(bot.filters_active).map(([k, v]) => (
                <Badge key={k} variant="outline" className={v ? 'border-emerald-500/50 text-emerald-400' : 'border-zinc-700 text-zinc-500'}>
                  {v ? <ShieldCheck className="w-3 h-3 mr-1" /> : <ShieldOff className="w-3 h-3 mr-1" />}
                  {k}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">Sin info de filtros.</p>
          )}
        </CardContent>
      </Card>

      {/* Posiciones abiertas */}
      {bot.open_positions && bot.open_positions.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100 text-base">
              Posiciones abiertas ({bot.open_positions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-400">Símbolo</TableHead>
                  <TableHead className="text-zinc-400">Dir.</TableHead>
                  <TableHead className="text-zinc-400">Entry</TableHead>
                  <TableHead className="text-zinc-400">Size</TableHead>
                  <TableHead className="text-zinc-400">Notional</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bot.open_positions.map((p, i) => (
                  <TableRow key={i} className="border-zinc-800">
                    <TableCell className="text-zinc-200 font-semibold">{p.symbol}</TableCell>
                    <TableCell>
                      <Badge className={p.direction === 'long' ? 'bg-emerald-600' : 'bg-rose-600'}>{p.direction}</Badge>
                    </TableCell>
                    <TableCell className="text-zinc-300 font-mono">{p.entry}</TableCell>
                    <TableCell className="text-zinc-300">{p.size}</TableCell>
                    <TableCell className="text-zinc-300">{formatUSD(p.notional_usd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Trades table */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base">Tabla de trades</CardTitle>
          <CardDescription>
            Buscar, ordenar, exportar a CSV
            {analytics?.best_trade && (
              <span className="ml-3 text-emerald-400 text-xs">
                Best: +{formatUSD(analytics.best_trade.pnl)} ({analytics.best_trade.market})
              </span>
            )}
            {analytics?.worst_trade && (
              <span className="ml-3 text-rose-400 text-xs">
                Worst: {formatUSD(analytics.worst_trade.pnl)} ({analytics.worst_trade.market})
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TradesTable trades={trades} botId={bot.bot_id} />
        </CardContent>
      </Card>
    </div>
  );
};

export { TimeframeSelector };
